import { useEffect, useState, useCallback } from 'react';
import Loader from '../../components/Loader/Loader';
import { useNavigate } from "react-router-dom";
import * as apicall from '../../components/Apicall';
import PhoneInput from 'react-phone-input-2'
import 'react-phone-input-2/lib/style.css';
import OtpInput from 'react-otp-input';
import { toast } from 'react-toastify';

const Login = () => {
    const [step, setStep] = useState(1);
    const [fullNumber, setFullNumber] = useState("");
    const [phoneNumber, setPhoneNumber] = useState("");
    const [countryCode, setCountryCode] = useState("");
    const [loader, setLoader] = useState(false);
    const [isNumberValid, setIsNumberValid] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [otp, setOtp] = useState('');
    const [count, setCount] = useState(120);
    const [otpRes, setOtpRes] = useState({});
    const [resendEnabled, setResendEnabled] = useState(false);

    const navigate = useNavigate();

    // Validate phone number input
    const numberOnChanges = async (value, country, formattedValue) => {
        setFullNumber(value);
        
        // Define minimum length requirements for each country
        const countryMinLengths = {
            'in': 10, // India: 10 digits
            'ae': 9   // UAE: 9 digits (excluding country code)
        };
        
        const minLength = countryMinLengths[country.countryCode] || 10;
        const phoneNumberWithoutCode = value.replace(country.dialCode, '').trim();
        
        if (phoneNumberWithoutCode.length >= minLength) {
            setIsNumberValid(true);
            setErrorMessage("");
            setCountryCode(country.dialCode);
            setPhoneNumber(phoneNumberWithoutCode);
        } else {
            setIsNumberValid(false);
            setErrorMessage(`Please enter a valid ${country.name} phone number (minimum ${minLength} digits)`);
        }
    };

    // Generate OTP
    const getOTP = async () => {
        if (!isNumberValid) {
            setErrorMessage("Please enter a valid phone number.");
            return;
        }

        try {
            setLoader(true);
            setCount(120);
            setResendEnabled(false);
            
            const data = {
                mobileNumber: fullNumber,
                is_mobile: false
            };
            
            const res = await apicall.post(data, "generateOTP");
            setOtpRes(res);
            setStep(2);
        } catch (e) {
            toast.error(e.message.replace('Error:', ''));
        } finally {
            setLoader(false);
        }
    };

    // Verify OTP
    const verifyOtp = async () => {
        if (otp.length !== 6) {
            setErrorMessage("Please enter 6-digit OTP.");
            return;
        }

        try {
            setLoader(true);
            
            const data = {
                mobileNumber: fullNumber,
                otp: otp,
                twilioService_id: otpRes.twilioService_id,
                user_id: otpRes.user_id,
                role: otpRes.role
            };
            
            await apicall.verifyOTP(data);
            const role = apicall.getRole();
            navigate(role === '3' ? '/dashboard' : '/vendors');
        } catch (e) {
            toast.error(e.message.replace('Error:', ''));
        } finally {
            setLoader(false);
        }
    };

    // Check if user is already logged in
    const isUserLoggedIn = useCallback(() => {
        const token = localStorage.getItem("token");
        if (token) {
            const role = apicall.getRole();
            navigate(role === '5' ? '/dashboard' : '/vendors');
        }
    }, [navigate]);

    // Handle Enter key press
    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !loader) {
            e.preventDefault();
            step === 1 ? getOTP() : verifyOtp();
        }
    };

    // Timer for OTP resend
    useEffect(() => {
        let timer;
        if (step === 2 && count > 0) {
            timer = setTimeout(() => setCount(c => c - 1), 1000);
        } else if (count === 0) {
            setResendEnabled(true);
        }
        return () => clearTimeout(timer);
    }, [count, step]);

    // Check auth status on mount
    useEffect(() => {
        isUserLoggedIn();
    }, [isUserLoggedIn]);

    // Format timer display
    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
    };

    // Custom OTP input renderer (without keydown handler)
    const renderOtpInput = (props) => <input {...props} />;

    return (
        <div className="container pt-5" onKeyDown={handleKeyDown}>
            <div className="d-flex justify-content-center pt-5">
                <div className="shadow-lg bg-body rounded sm-w-100" style={{ width: '500px' }}>
                    <div className="text-center">
                        <img src="/image/logo.png" alt="Logo" width={'60%'} />
                    </div>

                    <div className="p-5 pt-1">
                        {step === 1 ? (
                            <div>
                                <div className="">
                                    <label htmlFor="phone" className="label text-dark">
                                        Enter Mobile Number
                                    </label>
                                    <PhoneInput
                                        onlyCountries={['in', 'ae']}
                                        country={'in'}
                                        value={fullNumber}
                                        onChange={(value, country,formattedValue) => numberOnChanges(value, country,formattedValue)}
                                        inputStyle={{ width: "100%" }}
                                        enableSearch={false}
                                    />
                                    {errorMessage && (
                                        <div className="label text-danger">{errorMessage}</div>
                                    )}
                                </div>

                                <div className="text-center pt-4">
                                    <button
                                        style={{ marginLeft: "40%" }}
                                        className="btn btn-block primaryBackground text-white"
                                        onClick={getOTP}
                                        disabled={loader}
                                    >
                                        {loader ? <Loader style={{ width: 40, height: 20 }} /> : "Get OTP"}
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <div>
                                <div className="greyColor">
                                    Enter the code we just sent on your registered phone
                                </div>
                                <div className="primaryColorText">
                                    +{countryCode} {phoneNumber}
                                </div>

                                <div className="d-flex justify-content-center">
                                    <OtpInput
                                        value={otp}
                                        onChange={setOtp}
                                        numInputs={6}
                                        renderSeparator={<span>&nbsp;&nbsp;</span>}
                                        renderInput={renderOtpInput}
                                        containerStyle={{ width: "70%", height: '100px', alignItems: "center" }}
                                        inputStyle={{ width: "40%", height: "50%" }}
                                    />
                                </div>

                                {errorMessage && (
                                    <div className="label text-danger text-center">{errorMessage}</div>
                                )}

                                <div className="text-center">
                                    <button
                                        style={{ marginLeft: "40%" }}
                                        className="btn btn-block primaryBackground text-white"
                                        onClick={verifyOtp}
                                        disabled={loader || otp.length !== 6}
                                    >
                                        {loader ? <Loader style={{ width: 40, height: 20 }} /> : "Login"}
                                    </button>
                                </div>

                                {resendEnabled ? (
                                    <div
                                        className="text-center pt-2 primaryColorText"
                                        onClick={getOTP}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        <u>Resend OTP</u>
                                    </div>
                                ) : (
                                    <div className="greyColor text-center pt-2">
                                        Resend OTP in <span className="primaryColorText">{formatTime(count)}</span>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Login;


